import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { pipe, Array as A, Option as O } from 'effect'
import {
  isAuthenticatedPlayer,
  anonymousPlayer,
  invalidPlayer,
  isInitializedPlayer,
  isInvalidPlayer
} from 'src/types/domain/Player'
import type { Balance, InitializedPlayer, Player } from 'src/types/domain/Player'
import type { Bet } from 'src/types/domain/Bet'
import { getValidDecodedToken } from 'src/utils/getValidDecodedToken'
import { betStatusMap } from 'src/types/domain/BetStatus'
import { useDemoStore } from './demo'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'
import { useBetStore } from './bet'

setAutoFreeze(false)

interface PlayerState {
  currentPlayer: Player
}

export interface PlayerActions {
  authenticated: (player: Player) => void
  initialized: (player: InitializedPlayer) => void
  notAuthenticated: () => void
  invalid: () => void
  balanceChanged: (balance: Balance) => void
  displayNameChanged: (displayName: string) => void
  betPlaced: (bet: Bet, isFreeBet?: boolean) => void
  betWon: (bet: Bet) => void
  betCashedOut: (bet: Bet, payoutAmount: number) => void
  betCancelled: (bet: Bet) => void
  betRejected: (betAmount: number) => void
  betUnaccepted: (bet: Bet) => void
}

type PlayerStore = PlayerState & { actions: PlayerActions }

export const usePlayerStore = create<PlayerStore>()(
  devtools(
    immer((set) => ({
      currentPlayer: invalidPlayer,
      actions: {
        initialized: (player: InitializedPlayer) => {
          set(
            (state) => {
              state.currentPlayer = player
            },
            undefined,
            'setInitializedPlayer'
          )
        },
        authenticated: (player: Player) => {
          set(
            (state) => {
              state.currentPlayer = player
            },
            undefined,
            'setAuthenticatedPlayer'
          )
        },
        notAuthenticated: () => {
          set(
            (state) => {
              state.currentPlayer = anonymousPlayer
            },
            undefined,
            'setNotAuthenticatedPlayer'
          )
        },
        invalid: () => {
          set(
            (state) => {
              state.currentPlayer = invalidPlayer
            },
            undefined,
            'setInvalidPlayer'
          )
        },
        displayNameChanged: (displayName: string) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                state.currentPlayer.displayName = displayName
              }
            },
            undefined,
            'displayNameChanged'
          )
        },
        balanceChanged: (balance: Balance) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                state.currentPlayer.balance = balance
                // A real balance has arrived — mark it loaded so betting unlocks.
                // (This is the single point where the non-blocking-launch placeholder
                // balance flips to the real one, via the post-join balance fetch.)
                state.currentPlayer.balanceLoaded = true
              }
              // Update demo store
              useDemoStore.getState().actions.balanceChanged(balance)
            },
            undefined,
            'balanceChanged'
          )
        },
        betPlaced: (bet: Bet, isFreeBet?: boolean) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                // Only decrease balance if it's not a free bet
                if (!isFreeBet) {
                  const newBalance = {
                    ...state.currentPlayer.balance,
                    amount: state.currentPlayer.balance.amount - bet.betAmount
                  }

                  state.currentPlayer.balance = newBalance

                  // Update demo store
                  useDemoStore.getState().actions.balanceChanged(newBalance)
                }
              }
            },
            undefined,
            'betPlaced'
          )
        },
        betWon: (bet: Bet) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                const allBets = useBetStore.getState().bets
                const existingBet = pipe(
                  allBets.allIds,
                  A.map((id) => allBets.byId[id]),
                  A.map(O.fromNullable),
                  A.getSomes,
                  A.findFirst((b) => b.id === bet.id),
                  O.getOrUndefined
                )

                if (existingBet?.status === betStatusMap.WON) {
                  return state
                }

                const newBalance: Balance = {
                  ...state.currentPlayer.balance,
                  amount: state.currentPlayer.balance.amount + bet.potentialPayout
                }

                state.currentPlayer.balance = newBalance

                // Update demo store
                useDemoStore.getState().actions.balanceChanged(newBalance)
              }
            },
            undefined,
            'betWon'
          )
        },
        betCashedOut: (bet: Bet, payoutAmount: number) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                const allBets = useBetStore.getState().bets
                const existingBet = pipe(
                  allBets.allIds,
                  A.map((id) => allBets.byId[id]),
                  A.map(O.fromNullable),
                  A.getSomes,
                  A.findFirst((b) => b.id === bet.id),
                  O.getOrUndefined
                )

                // Prevent double-processing if bet already cashed out
                if (existingBet?.status === betStatusMap.CASHED_OUT) {
                  return state
                }

                const newBalance: Balance = {
                  ...state.currentPlayer.balance,
                  amount: state.currentPlayer.balance.amount + payoutAmount
                }

                state.currentPlayer.balance = newBalance

                // Update demo store
                useDemoStore.getState().actions.balanceChanged(newBalance)
              }
            },
            undefined,
            'betCashedOut'
          )
        },
        betCancelled: (bet: Bet) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                const allBets = useBetStore.getState().bets
                const existingBet = pipe(
                  allBets.allIds,
                  A.map((id) => allBets.byId[id]),
                  A.map(O.fromNullable),
                  A.getSomes,
                  A.findFirst((b) => b.id === bet.id),
                  O.getOrUndefined
                )

                if (existingBet?.status === betStatusMap.CANCELLED) {
                  return state
                }

                const newBalance: Balance = {
                  ...state.currentPlayer.balance,
                  amount: state.currentPlayer.balance.amount + bet.betAmount
                }

                state.currentPlayer.balance = newBalance

                // Update demo store
                useDemoStore.getState().actions.balanceChanged(newBalance)
              }
            },
            undefined,
            'betCancelled'
          )
        },
        betRejected: (betAmount: number) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                // Bets rejected via INSUFFICIENT_BALANCE_ERROR were never debited, so the
                // incoming betAmount may be undefined. Skip the additive refund in that
                // case — the server-side balance refresh in websocketEventHandlers will
                // reconcile state shortly.
                if (!Number.isFinite(betAmount)) {
                  return
                }

                const newBalance: Balance = {
                  ...state.currentPlayer.balance,
                  amount: state.currentPlayer.balance.amount + betAmount
                }

                state.currentPlayer.balance = newBalance

                // Update demo store
                useDemoStore.getState().actions.balanceChanged(newBalance)
              }
            },
            undefined,
            'betRejected'
          )
        },
        betUnaccepted: (bet: Bet) => {
          set(
            (state) => {
              if (isAuthenticatedPlayer(state.currentPlayer)) {
                // A free bet's stake was never debited from the wallet
                // (betPlaced's isFreeBet gate), so purging/timing-out an
                // unaccepted free bet must NOT credit that stake back as real
                // money. Only refund cash/booster-funded bets.
                if (bet.isFreeBet) return

                const newBalance: Balance = {
                  ...state.currentPlayer.balance,
                  amount: state.currentPlayer.balance.amount + bet.betAmount
                }

                state.currentPlayer.balance = newBalance

                // Update demo store
                useDemoStore.getState().actions.balanceChanged(newBalance)
              }
            },
            undefined,
            'betRejected'
          )
        }
      }
    })),
    { name: 'PlayerStore', enabled: isDevtoolsEnabled() }
  )
)

export const useGetCurrentPlayer = () => usePlayerStore((state) => state.currentPlayer)

export const useGetCurrentPlayerId = () =>
  usePlayerStore((state) => {
    if (isAuthenticatedPlayer(state.currentPlayer)) {
      return state.currentPlayer.id
    }

    return undefined
  })

export const useGetCurrentPlayerSessionId = () =>
  usePlayerStore((state) =>
    isAuthenticatedPlayer(state.currentPlayer) ? state.currentPlayer.sessionId : undefined
  )

export const useGetCurrentPlayerToken = () =>
  usePlayerStore((state) => {
    if (!isAuthenticatedPlayer(state.currentPlayer) && !isInitializedPlayer(state.currentPlayer)) {
      return undefined
    }

    // Check if token is valid
    const tokenData = getValidDecodedToken(state.currentPlayer.token)

    if (!tokenData) {
      state.actions.notAuthenticated()
      return undefined
    }

    return state.currentPlayer.token
  })

export const usePlayerActions = () => usePlayerStore((state) => state.actions)

export const useIsAuthenticated = () =>
  usePlayerStore((state) => isAuthenticatedPlayer(state.currentPlayer))

// True once the player's real balance has loaded. False during the brief
// non-blocking-launch window before the operator-wallet balance arrives —
// betting stays hard-blocked until this flips true.
export const useIsBalanceLoaded = () =>
  usePlayerStore((state) =>
    isAuthenticatedPlayer(state.currentPlayer) ? state.currentPlayer.balanceLoaded : false
  )

export const useIsInvalidPlayer = () =>
  usePlayerStore((state) => isInvalidPlayer(state.currentPlayer))

export const useIsInitializedPlayer = () =>
  usePlayerStore((state) => isInitializedPlayer(state.currentPlayer))

export const useIsDemoMode = () =>
  usePlayerStore(
    (state) =>
      isAuthenticatedPlayer(state.currentPlayer) &&
      state.currentPlayer.username.startsWith('_demoUser_')
  )

export const useGetCurrentPlayerDisplayName = () =>
  usePlayerStore((state) =>
    isAuthenticatedPlayer(state.currentPlayer) ? state.currentPlayer.displayName : undefined
  )
